/**
 * Pure helpers for presenting households and memberships.
 */

/** Two-letter initials for an avatar: "My Home" → "MH", "Smiths" → "SM". */
export const getInitials = (name: string | null | undefined): string => {
  const words = (name ?? '')
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

export type RoleBadgeTone = 'solid' | 'soft' | 'neutral' | 'purple' | 'success';

export interface RoleBadge {
  label: string;
  tone: RoleBadgeTone;
}

/**
 * Map a membership role to a badge. The household creator is the "Owner";
 * everyone else is labelled by role (Parent / Child / Cleaner), matching the
 * Members page role picker.
 */
export const getRoleBadge = (role: string | null | undefined, isOwner: boolean): RoleBadge => {
  if (isOwner) return { label: 'Owner', tone: 'solid' };
  switch (role) {
    case 'parent':
      return { label: 'Parent', tone: 'soft' };
    case 'child':
      return { label: 'Child', tone: 'success' };
    case 'cleaner':
      return { label: 'Cleaner', tone: 'purple' };
    default:
      return { label: 'Member', tone: 'neutral' };
  }
};

/** Number of avatar color variants available in the UI. */
export const AVATAR_TONE_COUNT = 6;

/** Deterministic avatar color index for an id/name (stable across renders). */
export const getAvatarToneIndex = (seed: string | null | undefined): number => {
  const s = seed ?? '';
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % AVATAR_TONE_COUNT;
};
